import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import classnames from 'classnames';
import ReactDOMServer from 'react-dom/server';

import {temporaryHtmlToString} from 'nutshell-core/list-items/temporary-html-to-string';
import {formatNumberWithMetrixPrefix} from 'nutshell-core/utils/format-number-with-metric-prefix';

import {ActionMenu} from '../../action-menu/action-menu';
import type {SelectMenuOption} from '../../action-menu/action-menu';
import {LeadAvatar} from '../../avatar/lead-avatar';
import {FlameIcon} from '../../icon/svg/flame-icon';
import {Link} from '../../link/link';

import {LeadFormattedValue} from '../lead-formatted-value';

import styles from './lead-card-header.module.css';

type Props = {
    id: string;
    leadName: string;
    leadStatus: string;
    avatarUrl: string;
    linkable: boolean;
    htmlUrlPath: string | null | undefined;
    priority: string | null | undefined;
    value: {
        value: number;
        prefix: string;
    };
    hasActionMenu: boolean;
    options?: SelectMenuOption[];
    onActionMenuOpen?: (leadId: string) => any;
    onNavigate?: () => void;
    anchorElementId?: string;
};

export function LeadCardHeader(props: Props) {
    const {
        id,
        leadName,
        leadStatus,
        avatarUrl,
        linkable,
        htmlUrlPath,
        priority,
        value,
        hasActionMenu,
        options,
        onActionMenuOpen,
        onNavigate,
        anchorElementId,
    } = props;

    const handleActionMenuOpen = () => {
        if (onActionMenuOpen) {
            onActionMenuOpen(id);
        }
    };

    const actionMenuOptions =
        options && options.length
            ? options
            : [{key: 'loading', label: 'Loading…', isDisabled: true}];

    const sanitizedLeadName = temporaryHtmlToString(leadName) || leadName;

    return (
        <div className={styles.container}>
            <div className={hasActionMenu ? styles['left--with-action-menu'] : styles.left}>
                <div className={styles.title}>
                    <div data-tip={leadStatus} className={styles.avatar}>
                        <LeadAvatar status={leadStatus} avatarUrl={avatarUrl} size={14} />
                    </div>
                    <div
                        data-html={true}
                        // Allows us to leverage React's automatic output escaping
                        // Prior to this, we were using dangerouslySetInnerHTML, which
                        // allowed for XSS attacks.
                        data-tip={ReactDOMServer.renderToString(<span>{sanitizedLeadName}</span>)}
                        className={styles.name}
                    >
                        {linkable && htmlUrlPath ? (
                            <Link
                                to={htmlUrlPath}
                                // @ts-expect-error
                                as={RoutedLink}
                                size='small'
                                onClick={() => {
                                    if (onNavigate) {
                                        onNavigate();
                                    }
                                }}
                            >
                                <div className='font-bold font-size-small truncate'>
                                    {sanitizedLeadName}
                                </div>
                            </Link>
                        ) : (
                            <div className='font-bold font-size-small truncate'>
                                {sanitizedLeadName}
                            </div>
                        )}
                    </div>
                    {priority === 'Hot' ? <FlameIcon size={14} alignCenter={true} /> : undefined}
                </div>
                {value && value.value ? (
                    <div
                        data-tip='Value'
                        className={classnames(hasActionMenu ? undefined : 'mr-16', styles.value)}
                    >
                        <LeadFormattedValue
                            formattedValue={formatNumberWithMetrixPrefix(value.value, value.prefix)}
                        />
                    </div>
                ) : undefined}
            </div>
            {hasActionMenu ? (
                <ActionMenu
                    onClick={handleActionMenuOpen}
                    options={actionMenuOptions}
                    size='tiny'
                    callMountId={anchorElementId}
                />
            ) : undefined}
        </div>
    );
}
